/**
 * Seeds of the member detail tabs that have no catalog of their own (Resources, Roles, Security).
 * Prototype: Diana Pérez reaches one dataset (ops_metrics) directly; most members have nothing yet.
 */

const DAY = 86_400_000;
const daysAgo = (n) => new Date(Date.now() - n * DAY).toISOString();

/** How long ago a seeded role was assigned (prototype: "Assigned by Admin · 14 days ago"). */
export const SEED_ROLE_ASSIGNMENT_DAYS = 14;
export const SEED_ROLE_ASSIGNED_BY = 'Admin';

/**
 * member_id → resources the member reaches. One row per access path: the same resource can come
 * directly and through a role (then removing the direct one leaves the other).
 * `source` is `direct`, `role` or `group`; `via` names the role or group of the other two.
 */
export const buildSeedMemberResources = () => ({
  // Diana Pérez: the prototype's single direct dataset.
  'mbr-014': [
    {
      id: 'res-014-ops-metrics',
      name: 'ops_metrics',
      type: 'dataset',
      access: 'Contributor',
      source: 'direct',
      via: null,
      granted_by: 'Maria García',
      granted_at: '2025-05-20T12:00:00.000Z',
    },
  ],
  // Sebastián Blanco: every type, and a resource reached by two paths.
  'mbr-016': [
    {
      id: 'res-016-customer-events',
      name: 'customer_events',
      type: 'dataset',
      access: 'Contributor',
      source: 'direct',
      via: null,
      granted_by: 'Maria García',
      granted_at: daysAgo(40),
    },
    {
      id: 'res-016-customer-events-role',
      name: 'customer_events',
      type: 'dataset',
      access: 'Viewer',
      source: 'role',
      via: 'Developer',
      granted_by: 'Admin',
      granted_at: daysAgo(90),
    },
    {
      id: 'res-016-churn-model',
      name: 'churn_model_v2',
      type: 'model',
      access: 'Viewer',
      source: 'direct',
      via: null,
      granted_by: 'Maria García',
      granted_at: daysAgo(21),
    },
    {
      id: 'res-016-billing-events',
      name: 'billing_events',
      type: 'event_bus',
      access: 'Publisher',
      source: 'group',
      via: 'Engineering',
      granted_by: 'Admin',
      granted_at: daysAgo(60),
    },
    {
      id: 'res-016-sandbox-eu',
      name: 'sandbox-eu-1',
      type: 'sandbox',
      access: 'Owner',
      source: 'direct',
      via: null,
      granted_by: 'Maria García',
      granted_at: daysAgo(9),
    },
  ],
  // Andrés Morales: one dataset.
  'mbr-006': [
    {
      id: 'res-006-claims-lake',
      name: 'claims_lake',
      type: 'dataset',
      access: 'Viewer',
      source: 'direct',
      via: null,
      granted_by: 'Maria García',
      granted_at: daysAgo(30),
    },
  ],
});

/** Devices a member signs in from; chosen by the member id so a member always shows the same one. */
const SESSION_POOL = Object.freeze([
  { device: 'Windows PC', browser: 'Chrome 125', location: 'Mexico City, MX' },
  { device: 'Windows Laptop', browser: 'Edge 124', location: 'Guadalajara, MX' },
  { device: 'MacBook Pro', browser: 'Safari 17', location: 'Bogotá, CO' },
  { device: 'MacBook Air', browser: 'Chrome 125', location: 'Medellín, CO' },
]);

export const sessionDeviceFor = (memberId) => {
  const n = Number(String(memberId).replace(/\D/g, '')) || 0;
  // Diana Pérez (mbr-014) is the prototype's Windows PC / Chrome / Mexico City.
  return memberId === 'mbr-014' ? SESSION_POOL[0] : SESSION_POOL[n % SESSION_POOL.length];
};
